import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { ClipboardList, Eye, Loader2 } from 'lucide-react';
import {
  getVisitPdfRevision,
  getVisitWorkLog,
  client,
} from '../../services/api';
import type { WorkLogEntryResponse } from '../../services/api/types.gen';
import {
  usePdfExport,
  handledExportError,
  type PdfSource,
} from '../../hooks/usePdfExport';
import { useToast } from '../../hooks/useToastContext';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { formatDateTimeInPT } from '../../utils/dateUtils';

interface WorkLogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visitId: string;
}

/** Stable id for an entry, used both as React key and PDF-export loading id. */
const entryId = (entry: WorkLogEntryResponse, index: number): string =>
  `${entry.event_type}-${entry.last_at}-${index}`;

export const WorkLogDialog: React.FC<WorkLogDialogProps> = ({
  open,
  onOpenChange,
  visitId,
}) => {
  const [entries, setEntries] = useState<WorkLogEntryResponse[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { exportPdf, exportingId } = usePdfExport();
  const { error: showError } = useToast();

  // The backend returns 422 when a chart has no audit history before the
  // chosen time (it predates history tracking). Surface that as its own clear
  // message and mark the failure handled so the generic toast is suppressed.
  const revisionUnavailable = useCallback(
    (errorOrResponse: unknown): Error | null => {
      if (getHttpStatus(errorOrResponse) !== 422) return null;
      showError(
        'Revision unavailable',
        'This chart has no saved history before that point, so an earlier ' +
          'version cannot be shown.'
      );
      return handledExportError('visit revision unavailable before as_of');
    },
    [showError]
  );

  // Open the chart's print view as it stood right after the given entry, by
  // asking the backend to reconstruct the visit at that entry's timestamp.
  const viewRevision = useCallback(
    (entry: WorkLogEntryResponse, index: number): void => {
      void exportPdf(
        entryId(entry, index),
        async (): Promise<PdfSource | null> => {
          let response;
          try {
            response = await getVisitPdfRevision({
              path: { visit_id: visitId },
              query: { as_of: entry.last_at },
              client,
            });
          } catch (err) {
            throw revisionUnavailable(err) ?? err;
          }
          if (response.data) {
            return {
              blob: new Blob([response.data as BlobPart], {
                type: 'application/pdf',
              }),
            };
          }
          const handled = revisionUnavailable(response);
          if (handled) throw handled;
          return null;
        },
        'chart revision PDF'
      );
    },
    [exportPdf, visitId, revisionUnavailable]
  );

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getVisitWorkLog({ path: { visit_id: visitId }, client })
      .then(res => {
        if (cancelled) return;
        setEntries(res.data ?? []);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load work log:', err);
        setError('Unable to load the work log. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, visitId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-[640px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <ClipboardList className='h-5 w-5 text-primary' />
            Work log
          </DialogTitle>
          <DialogDescription>
            Key events for this chart, oldest first.
          </DialogDescription>
        </DialogHeader>
        <div className='max-h-[60vh] overflow-y-auto pr-1'>
          {loading ? (
            <div className='flex items-center justify-center py-8 text-sm text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin mr-2' />
              Loading...
            </div>
          ) : error ? (
            <div className='py-6 text-sm text-destructive'>{error}</div>
          ) : !entries || entries.length === 0 ? (
            <div className='py-6 text-sm text-muted-foreground'>
              No activity has been recorded for this chart yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className='w-0' />
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry, index) => {
                  const id = entryId(entry, index);
                  const isExporting = exportingId === id;
                  return (
                    <TableRow key={id}>
                      <TableCell>
                        {entry.user_display_name ?? 'Unknown'}
                      </TableCell>
                      <TableCell>
                        {entry.label}
                        {entry.count > 1 ? ` (x${entry.count})` : ''}
                      </TableCell>
                      <TableCell>{formatDateTimeInPT(entry.last_at)}</TableCell>
                      <TableCell className='text-right'>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={() => viewRevision(entry, index)}
                          disabled={isExporting}
                          aria-label='View chart at this point'
                        >
                          {isExporting ? (
                            <Loader2 className='h-4 w-4 animate-spin' />
                          ) : (
                            <Eye className='h-4 w-4' />
                          )}
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
